import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Clock, LogIn, LogOut, ArrowLeft, Delete, Users, Lock } from "lucide-react";

// Afwezigheids-labels + kleuren (vakantie/ziek/vrij) voor het klok-scherm
const ABS = {
  vakantie: { label: "Vakantie", cls: "bg-amber-100 text-amber-700", border: "border-amber-200 bg-amber-50/60" },
  ziek: { label: "Ziek", cls: "bg-rose-100 text-rose-700", border: "border-rose-200 bg-rose-50/60" },
  vrij: { label: "Vrij", cls: "bg-emerald-100 text-emerald-700", border: "border-emerald-200 bg-emerald-50/60" },
};

// Klok-kiosk paneel — gebruikt als tab in de mobiele app (tablet op de vloer)
export default function KlokPaneel() {
  const [personeel, setPersoneel] = useState([]);
  const [aanwezig, setAanwezig] = useState([]);
  const [sel, setSel] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [zoek, setZoek] = useState("");
  const pollRef = useRef(null);

  const load = () => {
    api.get("/tijdklok/personeel").then((r) => { const today = new Date().toISOString().slice(0, 10); setPersoneel((r.data || []).filter((m) => !m.uitDienst || m.uitDienst > today)); }).catch(() => {});
    api.get("/tijdklok/status").then((r) => setAanwezig(r.data?.aanwezig || [])).catch(() => {});
  };
  useEffect(() => { load(); pollRef.current = setInterval(load, 20000); return () => clearInterval(pollRef.current); }, []);

  const aanwezigIds = useMemo(() => new Set(aanwezig.map((a) => String(a.medewerkerId))), [aanwezig]);
  const lijst = useMemo(() => personeel.filter((m) => m.naam.toLowerCase().includes(zoek.toLowerCase())).sort((a, b) => a.naam.localeCompare(b.naam)), [personeel, zoek]);

  const stempel = async (m, code) => {
    setBusy(true);
    try {
      const res = await api.post("/tijdklok/stempel", { medewerkerId: m.id, pin: code });
      const t = new Date(res.data.ts).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
      toast.success(res.data.actie === "in" ? `👋 ${res.data.naam} ingeklokt om ${t}` : `✅ ${res.data.naam} uitgeklokt om ${t}`);
      setSel(null); setPin(""); load();
    } catch (e) { toast.error(formatApiError(e)); setPin(""); }
    setBusy(false);
  };

  const tapPin = (d) => {
    if (d === "del") return setPin((p) => p.slice(0, -1));
    if (d === "clr") return setPin("");
    const np = (pin + d).slice(0, 4);
    setPin(np);
    if (np.length === 4 && sel && !busy) stempel(sel, np);
  };

  const isIn = sel && aanwezigIds.has(String(sel.id));

  useEffect(() => {
    if (sel?.onlyUitklokken && isIn && !busy) stempel(sel, "");
  }, [sel, isIn]);

  if (sel) {
    return (
      <section className="bg-white rounded-xl p-4 shadow-sm" data-testid="m-klok-pin">
        <button onClick={() => { setSel(null); setPin(""); }} className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-[#1C64B0]"><ArrowLeft className="h-4 w-4" /> Andere naam</button>
        <div className="text-center">
          <div className="text-2xl font-bold text-slate-900">{sel.naam}</div>
          <div className={`text-sm mb-4 font-semibold ${isIn ? "text-emerald-600" : "text-slate-500"}`}>{sel.onlyUitklokken ? "Noodherstel — dienst wordt nu veilig uitgeklokt" : (isIn ? "Ingeklokt — voer pin in om UIT te klokken" : "Voer je pincode in om IN te klokken")}</div>
          {sel.onlyUitklokken ? null : <>
          <div className="flex justify-center gap-3 mb-6">{[0, 1, 2, 3].map((i) => <div key={i} className={`h-4 w-4 rounded-full ${pin.length > i ? "bg-[#1C64B0]" : "bg-slate-200"}`} />)}</div>
          <div className="grid grid-cols-3 gap-2.5 max-w-xs mx-auto">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "clr", "0", "del"].map((d) => (
              <button key={d} disabled={busy} data-testid={`m-klok-pin-${d}`} onClick={() => tapPin(d)} className="h-16 rounded-xl bg-slate-100 border border-slate-200 text-2xl font-bold text-slate-800 active:scale-95 transition-transform flex items-center justify-center disabled:opacity-50">
                {d === "del" ? <Delete className="h-6 w-6" /> : d === "clr" ? "C" : d}
              </button>
            ))}
          </div>
          <div className="mt-4 text-xs text-slate-400 flex items-center justify-center gap-2">{isIn ? <LogOut className="h-4 w-4" /> : <LogIn className="h-4 w-4" />} {busy ? "Bezig…" : "4 cijfers = automatisch bevestigen"}</div>
          </>}
        </div>
      </section>
    );
  }

  return (
    <div className="space-y-3" data-testid="m-klok">
      <section className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3"><Clock className="h-5 w-5 text-[#1C64B0]" /><span className="font-bold text-slate-800">Tijdklok — tik je naam aan</span></div>
        <input data-testid="m-klok-zoek" value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek je naam…" className="w-full mb-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-base focus:outline-none focus:border-[#1C64B0]" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {lijst.map((m) => {
            const inn = aanwezigIds.has(String(m.id));
            const abs = m.afwezig ? (ABS[m.afwezig] || ABS.vrij) : null;
            if (abs) {
              return (
                <div key={m.id} data-testid="m-klok-naam-afwezig" title={`${m.naam} staat vandaag als ${abs.label.toLowerCase()} in de planning`} className={`rounded-xl p-3 text-left border cursor-not-allowed opacity-90 ${abs.border}`}>
                  <div className="font-bold text-slate-500 truncate flex items-center gap-1"><Lock className="h-3.5 w-3.5 shrink-0" />{m.naam}</div>
                  <div className="text-[11px] text-slate-400 truncate">{m.bureau || ""}</div>
                  <span className={`mt-1 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full ${abs.cls}`}>Afwezig · {abs.label}</span>
                </div>
              );
            }
            return (
              <button key={m.id} data-testid="m-klok-naam" onClick={() => { setSel(m); setPin(""); }} className={`rounded-xl p-3 text-left active:scale-95 transition-transform border ${inn ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
                <div className="font-bold text-slate-800 truncate">{m.naam}</div>
                <div className="text-[11px] text-slate-400 truncate">{m.bureau || ""}</div>
                <span className={`mt-1 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full ${inn ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>{inn ? "aanwezig" : "afwezig"}</span>
              </button>
            );
          })}
          {lijst.length === 0 && <div className="col-span-full text-center text-slate-400 py-8 text-sm">Geen medewerkers gevonden.</div>}
        </div>
      </section>
      <section className="bg-white rounded-xl p-4 shadow-sm" data-testid="m-klok-aanwezig">
        <div className="flex items-center gap-2 text-sm font-bold text-emerald-600 mb-2"><Users className="h-4 w-4" /> Nu aanwezig ({aanwezig.length})</div>
        <div className="flex flex-wrap gap-2">
          {aanwezig.length === 0 ? <span className="text-slate-400 text-sm">Niemand ingeklokt.</span> : aanwezig.map((a) => <span key={a.medewerkerId} className="text-xs bg-slate-100 rounded-full px-3 py-1 text-slate-600">{a.naam} · sinds {new Date(a.in).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</span>)}
        </div>
      </section>
    </div>
  );
}
